import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LoadingOverlay, Loader } from "react-overlay-loader";
import BootstrapTable, { ColumnDescription, SelectRowProps, SortOrder, TableChangeHandler } from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import "./styles.scss";
import Checkbox, { CheckBoxSize } from "../Common/ui/Checkbox";
import { UNLOCK_DEFAULT_PAGE_SIZE, UnlockLinkConstants, DateFormatConstants } from "src/Core/Common/Constants";
import { ILockLinkModel, initialLockLinkObject } from "src/Core/ViewModels/unlock/UnlockModel";
import { ApplicationState } from "src/store";
import { getUnlockLinks, updateUnlockLinks } from "src/actionCreators/unlockActionCreator";
import { UnLockIcon } from "src/assets/images/svg/IconCollection";
import { logger } from "src/routes/LoggedIn";
import { appInsightsData } from "src/helper/AppInsightsData";
import { viewingOthersInboxLabel } from "src/helper/HelperFunctions";
import { getFormattedLocalDateTime } from "src/helper/DateHelperFunctions";
import SwitchButtonGroup from "../Common/ui/Switch/SwitchButtonGroup";
import { UnlockType } from "../../Core/Common/Enums";

export const Unlock: React.FC = () => {
    const dispatch = useDispatch();
    const [unlockLinks, setUnlockLinks] = useState<ILockLinkModel[]>([initialLockLinkObject]);
    const [selectedUnlockLinks, setSelectedUnlockLinks] = useState<ILockLinkModel[] | []>([]);
    const [pagination, setPagination] = useState({
        pageSize: UNLOCK_DEFAULT_PAGE_SIZE,
        currentPage: 1,
        totalRecords: 0,
    });
    const [reload, setReload] = useState(false);
    const unlockStoreData = useSelector((state: ApplicationState) => state.unlockState);
    const { mailboxUserData } = useSelector((state: ApplicationState) => state.commonState);
    const isAnySelected = selectedUnlockLinks.length > 0;
    const [totalRecords, setTotalRecords] = useState(0);
    const customTotal = (from: number, to: number) => (
        <span
            style={{ display: totalRecords <= pagination.pageSize ? "none" : "block" }}
            className="total-count"
        >
            Showing <b>{from}</b> to <b>{to}</b> of <b>{totalRecords}</b> entries
        </span>
    );
    const tablePaginationOptions = {
        totalSize: pagination.totalRecords,
        sizePerPage: pagination.pageSize,
        page: pagination.currentPage,
        onPageChange: (page: number) => handleOnPageChange(page),
        paginationTotalRenderer: customTotal,
        showTotal: true,
        withFirstAndLast: true,
        alwaysShowAllBtns: true,
        hidePageListOnlyOnePage: true,
        hideSizePerPage: true,
    };
    const [sortState, setSortState] = useState({
        sortField: "",
        sortOrder: "desc" as SortOrder,
    });
    const [unlockToggle, setUnlockToggle]= useState<UnlockType>(UnlockType.Messages);
    const prevActiveTab = useRef(unlockToggle);

    useEffect(() => {
        // Page view Track Event call
        logger.trackPageView(appInsightsData.Unlock.PageTitle);
    },[]);

    useEffect(() => {
        // Fetch unlock links dispatch call
        dispatch(getUnlockLinks(unlockToggle));
    }, [reload]);

    useEffect(() => {
        if (unlockStoreData.unlockLinks) {
            setUnlockLinks(unlockStoreData.unlockLinks);
            setPagination({
                ...pagination,
                totalRecords: unlockStoreData.unlockLinks.length,
            });
            setTotalRecords(unlockStoreData.unlockLinks.length);
        }
        if (unlockToggle !== prevActiveTab.current) {
            handleOnPageChange(1);
            setSelectedUnlockLinks([]);
        }
        prevActiveTab.current = unlockToggle;
    }, [unlockStoreData]);

    useEffect(() => {
        setSortState({ sortField: "", sortOrder: "desc" });
    }, [prevActiveTab.current]);

    const subjectFormatter = (cell: string): JSX.Element => {
        return (
            <p title={cell} className="truncate unlock-cell">
                {cell}
            </p>
        );
    };
    const emailFormatter = (cell: string): JSX.Element => {
        return (
            <p title={cell} className="truncate unlock-cell">
                {cell}
            </p>
        );
    };

    const unlockFormatter = (row: ILockLinkModel): JSX.Element => {
        return (
            <div className="auth-questions-action-bar-container">
                <button
                    className="button-secondary-blue-outlined"
                    onClick={() => handleInlineUnlockClick(row)}
                    title={UnlockLinkConstants.UNLOCK_BUTTON_TITLE}
                >
                    {UnlockLinkConstants.UNLOCK_BUTTON_TITLE}
                </button>
            </div>
        );
    };
    const sentDateFormatter = (cell: string, row: ILockLinkModel): JSX.Element => {
        return (
            <div className="cell-actions-wrapper">
                <span>{getFormattedLocalDateTime(cell, DateFormatConstants.DEFAULT_DATE_FORMAT)}</span>
                {unlockFormatter(row)}
            </div>
        );
    };
    const handleInlineUnlockClick = (row: ILockLinkModel) => {
        setSelectedUnlockLinks([row]);
        handleUnlockClick([row]);
    };
    const HeaderActionsBar = (): JSX.Element => {
        return (
            <div className="header-actions-container">
                <span
                    className={isAnySelected ? "pointer" : "isDisabled"}
                    title={UnlockLinkConstants.UNLOCK_BUTTON_HOVER_TEXT}
                    onClick={(e: React.SyntheticEvent) => {
                        e.stopPropagation();
                        isAnySelected && handleUnlockClick();
                    }}
                >
                    <UnLockIcon
                        width={18}
                        height={18}
                        disabled={!isAnySelected}
                    />
                </span>
            </div>
        );
    };
    const columns: ColumnDescription[] = [
        {
            text: "Email Address",
            dataField: "emailAddress",
            sort: true,
            formatter: emailFormatter,
        },
        {
            text: "Subject",
            dataField: "subject",
            formatter: subjectFormatter,
            style: { width: "30%" },
            sort: true,
        },
        {
            text: "Date Sent",
            dataField: "dateSent",
            style: { width: "30%" },
            sort: true,
            formatter: sentDateFormatter,
        },

        {
            text: "",
            dataField: "unlock",
            style: { width: "0px" },
            headerFormatter: HeaderActionsBar,
        },
    ];

    const setNoContent = () => {
        if (!unlockStoreData.isLoading && unlockStoreData.unlockLinks.length === 0) {
            return UnlockLinkConstants.NO_DATA_TEXT;
        }
    };

    const handleOnSelect = (row: ILockLinkModel, isSelect: boolean) => {
        let selectedLinksCopy = [...selectedUnlockLinks];

        if (isSelect) {
            selectedLinksCopy.push(row);
        } else {
            selectedLinksCopy = selectedLinksCopy.filter(
                item => item.sentMessageRecipientId != row.sentMessageRecipientId
            );
        }
        setSelectedUnlockLinks(selectedLinksCopy);
    };

    const handleOnSelectAll = (isSelect: boolean, rows: ILockLinkModel[]) => {
        if (isSelect) {
            setSelectedUnlockLinks(rows);
        } else {
            setSelectedUnlockLinks([]);
        }
    };

    const handleUnlockClick = (rows?: ILockLinkModel[]) => {
        const payload = rows && rows.length > 0 ? rows : selectedUnlockLinks;
        // Unlock links dispatch call
        dispatch(
            updateUnlockLinks(payload,unlockToggle, (result: boolean) => {
                if (result) {
                    // Go to previous page if all the links are unlocked from that page
                    const currentPageRecords = unlockLinks.slice(
                        (pagination.currentPage - 1) * pagination.pageSize,
                        pagination.currentPage * pagination.pageSize
                    );
                    if (currentPageRecords.length === payload.length && pagination.currentPage > 1) {
                        handleOnPageChange(pagination.currentPage - 1);
                    }
                    setReload(reload => !reload);
                    setSelectedUnlockLinks([]);
                }
            })
        );
    };

    const handleOnPageChange = async (pageNo: number) => {
        setPagination({
            ...pagination,
            currentPage: pageNo,
        });
    };

    const handleChangeUnlockType =(unlock:UnlockType) => {    
        switch(unlock)
        {
            case UnlockType.Messages:
                    unlock = UnlockType.Requests;
                    break;
                case UnlockType.Requests:
                    unlock = UnlockType.Messages;
                    break;
                default:
                    unlock = UnlockType.Messages;
                    break;
        }
        setUnlockToggle(unlock);
        dispatch(getUnlockLinks(unlock));

    };

    const selectRow: SelectRowProps<any> = {
        mode: "checkbox",
        clickToSelect: true,
        nonSelectable: [],
        selected: selectedUnlockLinks.map(link => link.sentMessageRecipientId),
        onSelect: handleOnSelect,
        onSelectAll: handleOnSelectAll,
        selectionHeaderRenderer: ({ indeterminate }) => (
            <Checkbox
                id="mpiCheckbox"
                size={CheckBoxSize.sm}
                checked={!!selectedUnlockLinks.length}
                indeterminate={indeterminate}
                onClick={e => e.stopPropagation()}
                onChange={e => e.stopPropagation()}
            />
        ),
    };

    const handleTableChange : TableChangeHandler<any> | undefined = (type: string, { sortField, sortOrder }: any) => {
        if (type === "sort") {
            setSortState({
                sortField: sortField,
                sortOrder: sortOrder,
            });
            const sortedData = [...unlockLinks].sort((a, b) => {
                if (sortOrder === "asc") {
                    return a[sortField] > b[sortField] ? 1 : -1;
                } else {
                    return a[sortField] < b[sortField] ? 1 : -1;
                }
            });
            setUnlockLinks(sortedData);
        }
    };

    return (
        <LoadingOverlay className="unlock-loader">
           {mailboxUserData && (
                <p className="others-inbox-label-wrapper">
                        {viewingOthersInboxLabel(mailboxUserData.firstName, mailboxUserData.lastName)}
                </p>
                )}
                <div className="unlock-parent-container">
                <div>
                    <header>
                        <h3 className="unlock-header">{UnlockLinkConstants.UNLOCK_HEADER_TITLE_TEXT}</h3>
                        <div className="unlock-switch-div">
                        <SwitchButtonGroup
                                selected={UnlockType[unlockToggle]}
                                onSwitchChange={()=>{ handleChangeUnlockType(unlockToggle);}}                                
                                option1Text={UnlockType[UnlockType.Messages]}
                                option2Text={UnlockType[UnlockType.Requests]}
                            />                        
                        </div>
                        
                        
                        <span>{UnlockLinkConstants.UNLOCK_HEADER_HELPER_TEXT}</span>
                    </header>
                </div>
                <div className="unlock-table-container">
               
                    
                    <BootstrapTable
                        key={`${prevActiveTab.current}-${pagination.currentPage}-${totalRecords}`}
                        classes="settings-table"
                        keyField="sentMessageRecipientId"
                        columns={columns}
                        data={unlockLinks}
                        selectRow={selectRow}
                        noDataIndication={setNoContent()}
                        pagination={paginationFactory(tablePaginationOptions)}
                        bordered={false}
                        remote={{ sort: true }}
                        onTableChange={handleTableChange}
                        sort={{ dataField: sortState.sortField, order: sortState.sortOrder }}
                    />
                    </div>
                    </div>
            <Loader loading={unlockStoreData.isLoading} />
        </LoadingOverlay>
    );
};

